import { Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import { cn } from "../../lib/utils";
import { LanguageSelector } from "../ui/LanguageSelector";
import { ThemeToggle } from "../ui/ThemeToggle";
import { Logo } from "./Logo";

export const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isHomePage = location.pathname === "/";

  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when window is resized to desktop size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Smooth scroll function
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const targetId = e.currentTarget.getAttribute("href");
    if (!targetId) return;

    // If not on home page, navigate to home page first using React Router
    if (!isHomePage) {
      navigate(`/${targetId}`);
      return;
    }

    const targetElement = document.querySelector(targetId);
    if (!targetElement) return;

    const headerOffset = 80; // Adjust this value based on your header height
    const elementPosition = targetElement.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });

    // Close mobile menu if open
    setIsMobileMenuOpen(false);
  };

  // Handle hash scrolling after navigating back to the home page
  useEffect(() => {
    if (location.pathname === "/") {
      const hash = location.hash;
      if (hash) {
        setTimeout(() => {
          const targetElement = document.querySelector(hash);
          if (targetElement) {
            const headerOffset = 80;
            const elementPosition = targetElement.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth",
            });
          }
        }, 100);
      }
    }
  }, [location.pathname, location.hash]);

  // Render navigation link based on current page
  const NavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    if (isHomePage && to.startsWith("/#")) {
      return (
        <a href={to.substring(1)} onClick={handleSmoothScroll} className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400 font-medium transition-colors">
          {children}
        </a>
      );
    }
    return (
      <Link to={to} className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400 font-medium transition-colors">
        {children}
      </Link>
    );
  };

  // Render mobile navigation link based on current page
  const MobileNavLink = ({ to, children }: { to: string; children: React.ReactNode }) => {
    if (isHomePage && to.startsWith("/#")) {
      return (
        <a
          href={to.substring(1)}
          onClick={(e) => {
            handleSmoothScroll(e);
            setIsMobileMenuOpen(false);
          }}
          className="block py-2 text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400 font-medium"
        >
          {children}
        </a>
      );
    }
    return (
      <Link to={to} className="block py-2 text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400 font-medium" onClick={() => setIsMobileMenuOpen(false)}>
        {children}
      </Link>
    );
  };

  return (
    <nav className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300", isScrolled ? "bg-white/80 dark:bg-gray-900/80 backdrop-blur-md shadow-sm" : "bg-transparent")}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Logo />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <a href="#features" onClick={handleSmoothScroll} className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400 font-medium transition-colors">
              {t("nav.features")}
            </a>
            <NavLink to="/products">{t("nav.products")}</NavLink>
            <NavLink to="/document">{t("nav.documentation")}</NavLink>
            <NavLink to="/contact">{t("nav.contact")}</NavLink>
            <LanguageSelector />
            <ThemeToggle />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            <LanguageSelector />
            <ThemeToggle />
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md">
            <div className="flex flex-col space-y-4">
              <a
                href="#features"
                onClick={(e) => {
                  handleSmoothScroll(e);
                  setIsMobileMenuOpen(false);
                }}
                className="block py-2 text-gray-700 dark:text-gray-200 hover:text-brand-blue dark:hover:text-blue-400 font-medium"
              >
                {t("nav.features")}
              </a>
              <MobileNavLink to="/products">{t("nav.products")}</MobileNavLink>
              <MobileNavLink to="/document">{t("nav.documentation")}</MobileNavLink>
              <MobileNavLink to="/contact">{t("nav.contact")}</MobileNavLink>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
